// pages/[collection]/category/[category-slug].jsx
import React, { useState, useEffect } from 'react';
import { useRouter } from 'next/router';
import Head from 'next/head';
import Link from 'next/link';
import BaseLayout from '../../../layouts/BaseLayout.jsx';

const CollectionCategory = () => {
  const router = useRouter();
  const { collection, 'category-slug': categorySlug } = router.query;

  const [assetsData, setAssetsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Collection URL mappings - exactly like in SimilarIcons
  const collectionUrlMap = {
    basic: {
      baseUrl: 'https://icons-pbl.davooda.com/basic-icons/',
      imageSuffix: '.svg',
      type: 'svg',
      urlPrefix: '/basic-icons',
    },
    outline: {
      baseUrl: 'https://icons-pbl.davooda.com/outline/png-256px/',
      imageSuffix: '-256x256.png',
      type: 'png',
      urlPrefix: '/outline',
    },
    'polish-blue': {
      baseUrl: 'https://icons-pbl.davooda.com/polish-blue/png-256px/',
      imageSuffix: '-256x256.png',
      type: 'png',
      urlPrefix: '/polish-blue-icons',
    },
    'basic3d': {
      baseUrl: 'https://icons-pbl.davooda.com/basic3d-icons/png-256px/',
      imageSuffix: '.png',
      type: 'png',
      urlPrefix: '/basic3d',
    },
  };

  // URL collection mappings for API calls
  const urlCollectionMap = {
    'basic-icons': 'basic',
    'outline': 'outline', 
    'polish-blue-icons': 'polish-blue',
    'basic3d': 'basic3d',
  };

  // Get actual collection name for API call
  const getApiCollectionName = (urlCollection) => {
    return urlCollectionMap[urlCollection] || urlCollection;
  };

  // Get collection display name - original logic
  const getCollectionDisplayName = (collectionKey) => {
    // Handle special cases first
    if (collectionKey === 'outline') {
      return 'Outline Collection';
    }
    if (collectionKey === 'basic3d') {
      return 'Basic 3D Collection';
    }
    
    // Use the collection key from the URL (e.g., 'basic-icons')
    const baseName = collectionKey
      .replace('-icons', '')
      .replace('icons', '') // handle cases like 'basic-icons' -> 'basic'
      .split('-')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
    
    return `${baseName} Collection`;
  };

  // Helper function to get collection URL
  const getCollectionUrl = (collection) => {
    switch (collection) {
      case 'basic-icons':
        return '/basic-icons/';
      case 'outline':
        return '/outline/category/';
      case 'polish-blue-icons':
        return '/polish-blue-icons/';
      case 'basic3d':
        return '/basic3d/';
      default:
        return '/';
    }
  };

  // Helper function to format title with "icons" at the end
  const formatTitleWithIcons = (title) => {
    if (!title) return 'Icons';
    
    const lowerTitle = title.toLowerCase();
    if (lowerTitle.includes('icons')) {
      return title;
    }
    
    return `${title} Icons`;
  };

  // Generate BreadcrumbList schema
  const getBreadcrumbsJSON = () => {
    const collectionDisplayName = getCollectionDisplayName(collection);
    const collectionUrl = getCollectionUrl(collection);

    return {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Icons',
          item: 'https://davooda.com/',
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: collectionDisplayName,
          item: `https://davooda.com${collectionUrl}`,
        }
      ],
    };
  };

  // Fetch assets data
  useEffect(() => {
    if (!collection || !categorySlug) return;

    const fetchAssetsData = async () => {
      try {
        setLoading(true);
        setError(null); // Reset error on new fetch
        const apiCollection = getApiCollectionName(collection); // Use the specific API collection name

        const response = await fetch(`${process.env.NEXT_PUBLIC_BACKEND_URL}/category_assets`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            collection: apiCollection, // Use the mapped collection name for API
            category_slug: categorySlug,
            page: 1,
            limit: 1000 // Fetching a large number, consider pagination if needed for UI
          }),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ detail: 'Failed to fetch assets data' }));
          throw new Error(errorData.detail || `HTTP error ${response.status}`);
        }

        const data = await response.json();
        setAssetsData(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchAssetsData();
  }, [collection, categorySlug]);

  // Process asset for display - exactly like SimilarIcons logic
  const processAsset = (asset) => {
    if (!asset) return null;
    
    const assetCollection = asset.collection || 'Unknown';
    const isProt = assetCollection.startsWith('prot-');
    const mapKey = isProt ? `prot-${assetCollection.split('prot-')[1]}` : assetCollection;

    let src = '';
    if (collectionUrlMap[mapKey]) {
      src = `${collectionUrlMap[mapKey].baseUrl}${asset.slug}${collectionUrlMap[mapKey].imageSuffix}`;
    } else if (asset.url_small) {
      src = asset.url_small;
    } else {
      src = '/images/placeholder.png';
    }

    let url = '/icons';
    if (assetCollection === 'basic3d') {
      url = `/basic3d/${asset.slug}`;
    } else if (collectionUrlMap[mapKey] && collectionUrlMap[mapKey].urlPrefix) {
      url = `${collectionUrlMap[mapKey].urlPrefix}/${asset.slug}`;
    } else {
      url = asset.media_type === 'icon' ? `/icon/${asset.slug}` : `/photo/${asset.slug}`;
    }

    const width = mapKey === 'outline' || mapKey === 'polish-blue' ? 256 : asset.url_small_width || 100;
    const height = mapKey === 'outline' || mapKey === 'polish-blue' ? 256 : asset.url_small_height || 100;

    return {
      src: src,
      width: width,
      height: height,
      alt: asset.title || asset.slug || 'Asset',
      key: asset.slug || `asset-${asset.id}`,
      url: url,
      isFree: asset.is_free,
      collection: assetCollection, // Use the actual collection from asset data
      title: asset.title,
      type: collectionUrlMap[mapKey]?.type || 'png'
    };
  };

  // Check if icon is from basic collection (for SVG specific styling)
  // The 'collection' parameter here will be the key from router.query, e.g., 'basic-icons', 'outline'
  const isBasicSvgIconCollection = (collectionName) => {
    return collectionName === 'basic' || collectionName === 'basic-icons';
  };

  if (loading) {
    return (
      <BaseLayout
        title="Loading..."
        description="Loading category assets"
        showHeader={true}
      >
        <div className="page-container-with-top-margin">
          <div className="max-w-7xl w-[90%] mx-auto py-8">
            <div className="text-center">Loading...</div>
          </div>
        </div>
      </BaseLayout>
    );
  }

  if (error) {
    return (
      <BaseLayout
        title="Error"
        description="Error loading assets"
        showHeader={true}
      >
        <div className="page-container-with-top-margin">
          <div className="max-w-7xl w-[90%] mx-auto py-8">
            <div className="text-center text-red-500">Error: {error}</div>
          </div>
        </div>
      </BaseLayout>
    );
  }

  // The backend response structure for category_assets nests assets inside categories array.
  // Since we query for a specific category_slug, we expect one category in the response.
  const categoryData = assetsData?.categories?.[0];
  const collectionDisplayName = getCollectionDisplayName(collection); // 'collection' is from router.query
  const categoryDisplayNameFromData = categoryData?.category_name;
  const categoryDisplayTitle = categoryDisplayNameFromData || categorySlug?.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  const finalTitle = formatTitleWithIcons(categoryDisplayTitle);

  return (
    <BaseLayout
      title={`${finalTitle} - ${collectionDisplayName}`}
      description={`Browse ${finalTitle} from ${collectionDisplayName} collection`}
      showHeader={true}
    >
      <Head>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(getBreadcrumbsJSON()),
          }}
        />
      </Head>

      {/* Styles from SimilarIcons.jsx - exact copy */}
      <style jsx>{`
        .basic3d-icon {
          max-width: 65% !important;
          max-height: 65% !important;
          width: 65% !important;
          height: 65% !important;
          transform: scale(1.3);
        }
        .standard-icon {
          max-width: 50%;
          max-height: 50%;
        }
        .basic-svg-icon {
          width: 50%;
          height: 50%;
          object-fit: contain;
        }
        .basic3d-container {
          padding: 0 !important;
        }
        .icon-container {
          display: flex;
          align-items: center;
          justify-content: center;
        }
      `}</style>

      <div className="page-container-with-top-margin">
        <div className="max-w-7xl mx-auto py-8">
          {/* Breadcrumbs */}
          <nav className="mb-6">
            <ul className="list-none p-0 m-0 flex items-center text-base text-gray-600">
              <li className="flex items-center">
                <Link href="/" className="text-gray-600 hover:text-indigo-600 transition-colors">
                  Icons
                </Link>
                <span className="mx-2 text-gray-400">/</span>
              </li>
              <li className="flex items-center">
                <Link
                  href={getCollectionUrl(collection)}
                  className="text-gray-600 hover:text-indigo-600 transition-colors"
                >
                  {collectionDisplayName}
                </Link>
                <span className="mx-2 text-gray-400">/</span>
              </li>
            </ul>
          </nav>

          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold mb-2">{finalTitle}</h1>
            <p className="text-gray-600">
              {categoryData?.assets_count || 0} icons
            </p>
          </div>

          {/* Assets Grid - Matching SimilarIcons exactly */}
          {categoryData?.assets && categoryData.assets.length > 0 ? (
            <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-7 lg:grid-cols-10 gap-2">
              {categoryData.assets.map((asset) => {
                const processedAsset = processAsset(asset);
                if (!processedAsset) return null;
                
                // 'collection' here is the key from router.query (e.g., 'basic-icons', 'outline', 'basic3d')
                const isBasic3d = processedAsset.collection === 'basic3d';
                const isBasicSvg = isBasicSvgIconCollection(collection); // Use router collection, not asset collection
                
                return (
                  <div key={processedAsset.key} className="rounded-lg">
                    <Link href={processedAsset.url}>
                      <div className={`icon-container ${isBasic3d ? 'basic3d-container p-0' : 'p-1'} rounded-lg border border-gray-200 aspect-square group transition-transform duration-300`}>
                        {isBasicSvg ? (
                          // Special handling for basic SVG icons (e.g., basic-icons)
                          <div className="w-full h-full flex items-center justify-center">
                            <img
                              src={processedAsset.src}
                              alt={processedAsset.alt}
                              className="basic-svg-icon group-hover:scale-110 transition-transform duration-300"
                            />
                          </div>
                        ) : (
                          // Standard handling for other icons (PNGs like outline, polish-blue, basic3d)
                          <img
                            src={processedAsset.src}
                            alt={processedAsset.alt}
                            width="50"
                            height="50"
                            className={`${isBasic3d ? 'basic3d-icon' : 'standard-icon'} group-hover:scale-110 transition-transform duration-300`}
                            style={{
                              maxWidth: isBasic3d ? '65% !important' : '50%',
                              maxHeight: isBasic3d ? '65% !important' : '50%',
                              width: isBasic3d ? '65% !important' : 'auto',
                              height: isBasic3d ? '65% !important' : 'auto',
                            }}
                          />
                        )}
                        
                        {/* Free badge */}
                        {processedAsset.isFree && (
                          <div className="absolute top-1 right-1 bg-green-100 text-green-800 text-xs px-1 py-0.5 rounded">
                            Free
                          </div>
                        )}
                      </div>
                    </Link>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-500 text-lg">No assets found in this category.</p>
            </div>
          )}
        </div>
      </div>
    </BaseLayout>
  );
};

export default CollectionCategory;